import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectRates,
  selectSymbols,
  selectRatesReady,
  convertAmount,
  formatAmount,
  fetchRates,
} from "../../redux/ratesSlice";
import type { AppDispatch } from "../../redux/store";
import logo from "../../assets/cosmoola/logo.png";

/* ─── Injected global CSS (gradient border animation) ───── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  @property --gb-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  @keyframes gb-spin {
    to { --gb-angle: 360deg; }
  }

  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(1.4); }
  }

  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }

  .cosmoola-root * {
    box-sizing: border-box;
    font-family: 'DM Sans', system-ui, sans-serif;
  }

  /* ── Animated gradient border wrapper ── */
  .gb-wrap {
    position: relative;
    border-radius: var(--gb-radius, 14px);
    padding: var(--gb-pad, 1.5px);
    background: conic-gradient(
      from var(--gb-angle),
      #7c3aed, #06b6d4, #10b981, #f59e0b, #ef4444, #7c3aed
    );
    animation: gb-spin 3s linear infinite;
    z-index: 0;
  }

  .gb-wrap-slow {
    animation-duration: 6s;
  }

  .gb-inner {
    border-radius: calc(var(--gb-radius, 14px) - var(--gb-pad, 1.5px));
    background: var(--gb-bg, #fff);
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }

  /* ── Input rows: gradient border on hover/focus ── */
  .conv-input-row {
    position: relative;
    border-radius: 10px;
    background: #fff;
    border: 1px solid #e4e4e7;
    transition: border-color 0.2s;
    overflow: hidden;
  }

  .conv-input-row::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 11px;
    padding: 1.5px;
    background: conic-gradient(
      from var(--gb-angle),
      #7c3aed, #06b6d4, #10b981, #f59e0b, #7c3aed
    );
    animation: gb-spin 3s linear infinite;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.25s;
  }

  .conv-input-row:hover::before,
  .conv-input-row:focus-within::before {
    opacity: 1;
  }

  /* ── Pair rows hover ── */
  .pair-row-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 0;
    border-bottom: 1px solid #f4f4f5;
    cursor: pointer;
    border-radius: 8px;
    transition: background 0.15s, padding 0.15s;
  }
  .pair-row-item:hover {
    background: #faf5ff;
    padding-left: 8px;
    padding-right: 8px;
  }

  /* ── Calc keys ── */
  .calc-key {
    background: #fafafa;
    border: none;
    color: #111;
    font-size: 18px;
    height: 56px;
    cursor: pointer;
    font-family: 'DM Sans', system-ui, sans-serif;
    transition: background 0.1s, transform 0.08s;
    border-right: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
  }
  .calc-key:active { transform: scale(0.94); }
  .calc-key.fn  { background: #f4f4f5; color: #52525b; font-size: 15px; }
  .calc-key.fn:hover  { background: #e4e4e7; }
  .calc-key.op  { background: #faf5ff; color: #7c3aed; font-weight: 600; }
  .calc-key.op:hover  { background: #ede9fe; }
  .calc-key.num:hover { background: #f0f0f0; }
  .calc-key.eq  {
    background: linear-gradient(135deg, #7c3aed, #5b21b6);
    color: #fff;
    font-size: 22px;
    border: none;
  }
  .calc-key.eq:hover { filter: brightness(1.1); }
  .calc-key.zero { grid-column: span 2; }

  /* ── Swap btn ── */
  .swap-btn {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 1.5px solid #7c3aed;
    background: #faf5ff;
    color: #7c3aed;
    font-size: 17px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: transform 0.3s, background 0.2s;
  }
  .swap-btn:hover { background: #ede9fe; transform: rotate(180deg); }

  /* ── Send btn shimmer ── */
  .send-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    font-family: 'DM Sans', system-ui, sans-serif;
    letter-spacing: 0.3px;
    color: #fff;
    background: linear-gradient(
      270deg,
      #7c3aed, #06b6d4, #10b981, #7c3aed
    );
    background-size: 300% 300%;
    animation: shimmer 4s linear infinite;
    transition: opacity 0.2s;
    margin-top: 16px;
  }
  .send-btn:hover { opacity: 0.9; }

  /* ── Modal ── */
  .picker-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 1000;
    display: flex; align-items: flex-end; justify-content: center;
    opacity: 0; pointer-events: none;
    transition: opacity 0.2s;
  }
  .picker-overlay.open { opacity: 1; pointer-events: all; }
  .picker-sheet {
    background: #fff;
    border-radius: 20px 20px 0 0;
    width: 100%; max-width: 500px;
    max-height: 72vh;
    display: flex; flex-direction: column;
    overflow: hidden;
    border-top: 1.5px solid #7c3aed;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  .picker-overlay.open .picker-sheet { transform: translateY(0); }

  /* ── Nav btn ── */
  .nav-btn {
    padding: 7px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12);
    background: transparent;
    color: #aaa;
    font-size: 13px;
    cursor: pointer;
    font-family: 'DM Sans', system-ui, sans-serif;
    transition: all 0.2s;
  }
  .nav-btn:hover { background: rgba(255,255,255,0.07); color: #f0f0f0; }
  .nav-btn.active { background: rgba(124,58,237,0.18); color: #a78bfa; border-color: rgba(124,58,237,0.35); }

  @media (max-width: 700px) {
    .cosmoola-two-col { grid-template-columns: 1fr !important; }
    .cosmoola-calc-card { position: static !important; }
    .cosmoola-hero h1 { font-size: 28px !important; }
  }
`;

/* ─── Types ─────────────────────────────────────────────── */
interface CurrencyItem {
  code: string;
  flag: string;
  name: string;
}

/* ─── Full world currency list (150+) ───────────────────── */
const CURRENCIES: CurrencyItem[] = [
  { code: "USD", flag: "🇺🇸", name: "US Dollar" },
  { code: "EUR", flag: "🇪🇺", name: "Euro" },
  { code: "GBP", flag: "🇬🇧", name: "British Pound" },
  { code: "JPY", flag: "🇯🇵", name: "Japanese Yen" },
  { code: "AUD", flag: "🇦🇺", name: "Australian Dollar" },
  { code: "CAD", flag: "🇨🇦", name: "Canadian Dollar" },
  { code: "CHF", flag: "🇨🇭", name: "Swiss Franc" },
  { code: "CNY", flag: "🇨🇳", name: "Chinese Yuan" },
  { code: "INR", flag: "🇮🇳", name: "Indian Rupee" },
  { code: "BRL", flag: "🇧🇷", name: "Brazilian Real" },
  { code: "MXN", flag: "🇲🇽", name: "Mexican Peso" },
  { code: "SGD", flag: "🇸🇬", name: "Singapore Dollar" },
  { code: "HKD", flag: "🇭🇰", name: "Hong Kong Dollar" },
  { code: "NOK", flag: "🇳🇴", name: "Norwegian Krone" },
  { code: "SEK", flag: "🇸🇪", name: "Swedish Krona" },
  { code: "DKK", flag: "🇩🇰", name: "Danish Krone" },
  { code: "NZD", flag: "🇳🇿", name: "New Zealand Dollar" },
  { code: "KRW", flag: "🇰🇷", name: "South Korean Won" },
  { code: "TRY", flag: "🇹🇷", name: "Turkish Lira" },
  { code: "ZAR", flag: "🇿🇦", name: "South African Rand" },
  { code: "AED", flag: "🇦🇪", name: "UAE Dirham" },
  { code: "SAR", flag: "🇸🇦", name: "Saudi Riyal" },
  { code: "QAR", flag: "🇶🇦", name: "Qatari Rial" },
  { code: "KWD", flag: "🇰🇼", name: "Kuwaiti Dinar" },
  { code: "BHD", flag: "🇧🇭", name: "Bahraini Dinar" },
  { code: "OMR", flag: "🇴🇲", name: "Omani Rial" },
  { code: "NGN", flag: "🇳🇬", name: "Nigerian Naira" },
  { code: "KES", flag: "🇰🇪", name: "Kenyan Shilling" },
  { code: "GHS", flag: "🇬🇭", name: "Ghanaian Cedi" },
  { code: "TZS", flag: "🇹🇿", name: "Tanzanian Shilling" },
  { code: "UGX", flag: "🇺🇬", name: "Ugandan Shilling" },
  { code: "RWF", flag: "🇷🇼", name: "Rwandan Franc" },
  { code: "ETB", flag: "🇪🇹", name: "Ethiopian Birr" },
  { code: "EGP", flag: "🇪🇬", name: "Egyptian Pound" },
  { code: "MAD", flag: "🇲🇦", name: "Moroccan Dirham" },
  { code: "DZD", flag: "🇩🇿", name: "Algerian Dinar" },
  { code: "TND", flag: "🇹🇳", name: "Tunisian Dinar" },
  { code: "LYD", flag: "🇱🇾", name: "Libyan Dinar" },
  { code: "XOF", flag: "🇸🇳", name: "CFA Franc BCEAO" },
  { code: "XAF", flag: "🇨🇲", name: "CFA Franc BEAC" },
  { code: "AOA", flag: "🇦🇴", name: "Angolan Kwanza" },
  { code: "CDF", flag: "🇨🇩", name: "Congolese Franc" },
  { code: "ZMW", flag: "🇿🇲", name: "Zambian Kwacha" },
  { code: "MWK", flag: "🇲🇼", name: "Malawian Kwacha" },
  { code: "MZN", flag: "🇲🇿", name: "Mozambican Metical" },
  { code: "BWP", flag: "🇧🇼", name: "Botswana Pula" },
  { code: "NAD", flag: "🇳🇦", name: "Namibian Dollar" },
  { code: "ZWL", flag: "🇿🇼", name: "Zimbabwean Dollar" },
  { code: "SDG", flag: "🇸🇩", name: "Sudanese Pound" },
  { code: "SSP", flag: "🇸🇸", name: "South Sudanese Pound" },
  { code: "SOS", flag: "🇸🇴", name: "Somali Shilling" },
  { code: "ERN", flag: "🇪🇷", name: "Eritrean Nakfa" },
  { code: "DJF", flag: "🇩🇯", name: "Djiboutian Franc" },
  { code: "BIF", flag: "🇧🇮", name: "Burundian Franc" },
  { code: "GNF", flag: "🇬🇳", name: "Guinean Franc" },
  { code: "SLL", flag: "🇸🇱", name: "Sierra Leonean Leone" },
  { code: "GMD", flag: "🇬🇲", name: "Gambian Dalasi" },
  { code: "MGA", flag: "🇲🇬", name: "Malagasy Ariary" },
  { code: "MUR", flag: "🇲🇺", name: "Mauritian Rupee" },
  { code: "SCR", flag: "🇸🇨", name: "Seychellois Rupee" },
  { code: "KMF", flag: "🇰🇲", name: "Comorian Franc" },
  { code: "STN", flag: "🇸🇹", name: "São Tomé Dobra" },
  { code: "CVE", flag: "🇨🇻", name: "Cape Verdean Escudo" },
  { code: "LRD", flag: "🇱🇷", name: "Liberian Dollar" },
  { code: "SZL", flag: "🇸🇿", name: "Swazi Lilangeni" },
  { code: "LSL", flag: "🇱🇸", name: "Lesotho Loti" },
  { code: "IDR", flag: "🇮🇩", name: "Indonesian Rupiah" },
  { code: "MYR", flag: "🇲🇾", name: "Malaysian Ringgit" },
  { code: "THB", flag: "🇹🇭", name: "Thai Baht" },
  { code: "PHP", flag: "🇵🇭", name: "Philippine Peso" },
  { code: "VND", flag: "🇻🇳", name: "Vietnamese Dong" },
  { code: "PKR", flag: "🇵🇰", name: "Pakistani Rupee" },
  { code: "BDT", flag: "🇧🇩", name: "Bangladeshi Taka" },
  { code: "LKR", flag: "🇱🇰", name: "Sri Lankan Rupee" },
  { code: "NPR", flag: "🇳🇵", name: "Nepalese Rupee" },
  { code: "MMK", flag: "🇲🇲", name: "Myanmar Kyat" },
  { code: "KHR", flag: "🇰🇭", name: "Cambodian Riel" },
  { code: "LAK", flag: "🇱🇦", name: "Laotian Kip" },
  { code: "TWD", flag: "🇹🇼", name: "New Taiwan Dollar" },
  { code: "MNT", flag: "🇲🇳", name: "Mongolian Tugrik" },
  { code: "AFN", flag: "🇦🇫", name: "Afghan Afghani" },
  { code: "BTN", flag: "🇧🇹", name: "Bhutanese Ngultrum" },
  { code: "MVR", flag: "🇲🇻", name: "Maldivian Rufiyaa" },
  { code: "BND", flag: "🇧🇳", name: "Brunei Dollar" },
  { code: "PGK", flag: "🇵🇬", name: "Papua New Guinean Kina" },
  { code: "FJD", flag: "🇫🇯", name: "Fijian Dollar" },
  { code: "TOP", flag: "🇹🇴", name: "Tongan Paʻanga" },
  { code: "WST", flag: "🇼🇸", name: "Samoan Tala" },
  { code: "VUV", flag: "🇻🇺", name: "Vanuatu Vatu" },
  { code: "SBD", flag: "🇸🇧", name: "Solomon Islands Dollar" },
  { code: "XPF", flag: "🇵🇫", name: "CFP Franc" },
  { code: "MOP", flag: "🇲🇴", name: "Macanese Pataca" },
  { code: "KPW", flag: "🇰🇵", name: "North Korean Won" },
  { code: "ILS", flag: "🇮🇱", name: "Israeli Shekel" },
  { code: "IRR", flag: "🇮🇷", name: "Iranian Rial" },
  { code: "IQD", flag: "🇮🇶", name: "Iraqi Dinar" },
  { code: "JOD", flag: "🇯🇴", name: "Jordanian Dinar" },
  { code: "LBP", flag: "🇱🇧", name: "Lebanese Pound" },
  { code: "SYP", flag: "🇸🇾", name: "Syrian Pound" },
  { code: "YER", flag: "🇾🇪", name: "Yemeni Rial" },
  { code: "PLN", flag: "🇵🇱", name: "Polish Zloty" },
  { code: "CZK", flag: "🇨🇿", name: "Czech Koruna" },
  { code: "HUF", flag: "🇭🇺", name: "Hungarian Forint" },
  { code: "RON", flag: "🇷🇴", name: "Romanian Leu" },
  { code: "BGN", flag: "🇧🇬", name: "Bulgarian Lev" },
  { code: "ISK", flag: "🇮🇸", name: "Icelandic Króna" },
  { code: "UAH", flag: "🇺🇦", name: "Ukrainian Hryvnia" },
  { code: "RSD", flag: "🇷🇸", name: "Serbian Dinar" },
  { code: "BAM", flag: "🇧🇦", name: "Bosnia-Herzegovina Mark" },
  { code: "MKD", flag: "🇲🇰", name: "Macedonian Denar" },
  { code: "ALL", flag: "🇦🇱", name: "Albanian Lek" },
  { code: "MDL", flag: "🇲🇩", name: "Moldovan Leu" },
  { code: "BYN", flag: "🇧🇾", name: "Belarusian Ruble" },
  { code: "GEL", flag: "🇬🇪", name: "Georgian Lari" },
  { code: "AMD", flag: "🇦🇲", name: "Armenian Dram" },
  { code: "AZN", flag: "🇦🇿", name: "Azerbaijani Manat" },
  { code: "GIP", flag: "🇬🇮", name: "Gibraltar Pound" },
  { code: "KZT", flag: "🇰🇿", name: "Kazakhstani Tenge" },
  { code: "UZS", flag: "🇺🇿", name: "Uzbekistani Som" },
  { code: "KGS", flag: "🇰🇬", name: "Kyrgyzstani Som" },
  { code: "TJS", flag: "🇹🇯", name: "Tajikistani Somoni" },
  { code: "TMT", flag: "🇹🇲", name: "Turkmenistani Manat" },
  { code: "ARS", flag: "🇦🇷", name: "Argentine Peso" },
  { code: "CLP", flag: "🇨🇱", name: "Chilean Peso" },
  { code: "COP", flag: "🇨🇴", name: "Colombian Peso" },
  { code: "PEN", flag: "🇵🇪", name: "Peruvian Sol" },
  { code: "UYU", flag: "🇺🇾", name: "Uruguayan Peso" },
  { code: "PYG", flag: "🇵🇾", name: "Paraguayan Guarani" },
  { code: "BOB", flag: "🇧🇴", name: "Bolivian Boliviano" },
  { code: "VES", flag: "🇻🇪", name: "Venezuelan Bolívar" },
  { code: "GTQ", flag: "🇬🇹", name: "Guatemalan Quetzal" },
  { code: "HNL", flag: "🇭🇳", name: "Honduran Lempira" },
  { code: "CRC", flag: "🇨🇷", name: "Costa Rican Colón" },
  { code: "NIO", flag: "🇳🇮", name: "Nicaraguan Córdoba" },
  { code: "DOP", flag: "🇩🇴", name: "Dominican Peso" },
  { code: "JMD", flag: "🇯🇲", name: "Jamaican Dollar" },
  { code: "TTD", flag: "🇹🇹", name: "Trinidad & Tobago Dollar" },
  { code: "BBD", flag: "🇧🇧", name: "Barbadian Dollar" },
  { code: "XCD", flag: "🇦🇬", name: "East Caribbean Dollar" },
  { code: "HTG", flag: "🇭🇹", name: "Haitian Gourde" },
  { code: "BSD", flag: "🇧🇸", name: "Bahamian Dollar" },
  { code: "BZD", flag: "🇧🇿", name: "Belize Dollar" },
  { code: "GYD", flag: "🇬🇾", name: "Guyanese Dollar" },
  { code: "SRD", flag: "🇸🇷", name: "Surinamese Dollar" },
  { code: "AWG", flag: "🇦🇼", name: "Aruban Florin" },
  { code: "ANG", flag: "🇨🇼", name: "Netherlands Antillean Guilder" },
  { code: "BMD", flag: "🇧🇲", name: "Bermudian Dollar" },
  { code: "KYD", flag: "🇰🇾", name: "Cayman Islands Dollar" },
  { code: "PAB", flag: "🇵🇦", name: "Panamanian Balboa" },
  { code: "CUP", flag: "🇨🇺", name: "Cuban Peso" },
  { code: "FKP", flag: "🇫🇰", name: "Falkland Islands Pound" },
  { code: "SHP", flag: "🇸🇭", name: "Saint Helena Pound" },
  { code: "MRU", flag: "🇲🇷", name: "Mauritanian Ouguiya" },
];

const POPULAR_PAIRS = ["USD","GBP","EUR","AED","CAD","SAR","KES","ZAR","INR","CNY"];

function getCurrency(code: string): CurrencyItem {
  return CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0];
}

/* ─── Calculator hook ────────────────────────────────────── */
function useCalculator() {
  const [expr,   setExpr]   = useState("");
  const [result, setResult] = useState("0");
  const [justEq, setJustEq] = useState(false);

  const safeEval = (e: string): number | null => {
    try {
      // eslint-disable-next-line no-new-func
      const v = Function(
        '"use strict";return (' +
          e.replace(/÷/g, "/").replace(/×/g, "*").replace(/−/g, "-") +
          ")"
      )();
      return isFinite(v) ? v : null;
    } catch {
      return null;
    }
  };

  const input = (v: string) => {
    let e = expr;
    if (justEq && /[0-9.]/.test(v)) { e = ""; setJustEq(false); }
    e += v;
    setExpr(e);
    const r = safeEval(e);
    if (r !== null) setResult(parseFloat(r.toFixed(8)).toString());
  };

  const fn = (v: string) => {
    if (v === "AC") { setExpr(""); setResult("0"); setJustEq(false); return; }
    if (v === "⌫") {
      const e = expr.slice(0, -1);
      setExpr(e);
      const r = safeEval(e);
      if (r !== null) setResult(parseFloat(r.toFixed(8)).toString());
      return;
    }
    if (v === "=") {
      const r = safeEval(expr);
      if (r !== null) {
        const s = parseFloat(r.toFixed(8)).toString();
        setResult(s); setExpr(s); setJustEq(true);
      }
      return;
    }
    if (v === "+/-") {
      const r = safeEval(expr);
      if (r !== null) {
        const s = parseFloat((-r).toFixed(8)).toString();
        setResult(s); setExpr(s);
      }
      return;
    }
    if (v === "%") {
      const r = safeEval(expr);
      if (r !== null) {
        const s = parseFloat((r / 100).toFixed(8)).toString();
        setResult(s); setExpr(s);
      }
      return;
    }
  };

  return { expr, result, input, fn };
}

/* ─── Currency Picker Modal ──────────────────────────────── */
function CurrencyPickerModal({
  visible, selected, onSelect, onClose, rates, symbols, ratesReady,
}: {
  visible: boolean;
  selected: string;
  onSelect: (c: string) => void;
  onClose: () => void;
  rates: Record<string, number>;
  symbols: Record<string, string>;
  ratesReady: boolean;
}) {
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (visible) { setSearch(""); setTimeout(() => inputRef.current?.focus(), 320); }
  }, [visible]);

  useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  const filtered = useMemo(
    () => CURRENCIES.filter((c) =>
      `${c.code} ${c.name}`.toLowerCase().includes(search.toLowerCase())
    ),
    [search]
  );

  return (
    <div className={`picker-overlay${visible ? " open" : ""}`} onClick={onClose}>
      <div className="picker-sheet" onClick={(e) => e.stopPropagation()}>
        {/* handle */}
        <div style={{ width: 36, height: 3, background: "#d4d4d8", borderRadius: 2, margin: "14px auto 0", flexShrink: 0 }} />
        <p style={{ fontSize: 16, fontWeight: 600, textAlign: "center", padding: "12px 20px", color: "#09090b", flexShrink: 0 }}>
          Select Currency
        </p>
        <input
          ref={inputRef}
          style={{
            margin: "0 16px 10px",
            padding: "10px 14px",
            border: "1px solid #e4e4e7",
            borderRadius: 8,
            fontSize: 14,
            background: "#fafafa",
            color: "#09090b",
            outline: "none",
            flexShrink: 0,
            fontFamily: "'DM Sans', system-ui, sans-serif",
          }}
          placeholder="Search currency…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ overflowY: "auto", flex: 1 }}>
          {filtered.map((c) => (
            <div
              key={c.code}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "11px 20px",
                borderBottom: "1px solid #f4f4f5",
                cursor: "pointer",
                background: c.code === selected ? "#faf5ff" : undefined,
                transition: "background 0.1s",
              }}
              onClick={() => { onSelect(c.code); onClose(); }}
              onMouseEnter={(e) => { if (c.code !== selected) (e.currentTarget as HTMLDivElement).style.background = "#fafafa"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = c.code === selected ? "#faf5ff" : ""; }}
            >
              <span style={{ fontSize: 22 }}>{c.flag}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#09090b" }}>{c.code}</div>
                <div style={{ fontSize: 11, color: "#a1a1aa", marginTop: 1 }}>{c.name}</div>
              </div>
              {ratesReady && rates[c.code] ? (
                <span style={{ fontSize: 13, fontWeight: 500, color: "#7c3aed" }}>
                  {formatAmount(convertAmount(1, "USD", c.code, rates), c.code, symbols)}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Calc pad layout — matches RN exactly ──────────────── */
const CALC_PAD: [string, "fn" | "op" | "num" | "eq"][][] = [
  [["AC","fn"],["+/-","fn"],["%","fn"],["÷","op"]],
  [["7","num"],["8","num"],["9","num"],["×","op"]],
  [["4","num"],["5","num"],["6","num"],["−","op"]],
  [["1","num"],["2","num"],["3","num"],["+","op"]],
  [["0","num"],[".","num"],["⌫","fn"],["=","eq"]],
];

/* ─── Style injector ─────────────────────────────────────── */
function GlobalStyles() {
  useEffect(() => {
    const tag = document.createElement("style");
    tag.id = "cosmoola-styles";
    tag.textContent = GLOBAL_CSS;
    if (!document.getElementById("cosmoola-styles")) {
      document.head.appendChild(tag);
    }
    return () => { document.getElementById("cosmoola-styles")?.remove(); };
  }, []);
  return null;
}

/* ─── Main Component ─────────────────────────────────────── */
export default function Converter() {
  const dispatch   = useDispatch<AppDispatch>();
  const rates      = useSelector(selectRates);
  const symbols    = useSelector(selectSymbols);
  const ratesReady = useSelector(selectRatesReady);
  const calc       = useCalculator();

  const [fromCurrency,   setFromCurrency]   = useState("EUR");
  const [toCurrency,     setToCurrency]     = useState("KES");
  const [fromAmount,     setFromAmount]     = useState("1000");
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker,   setShowToPicker]   = useState(false);

  useEffect(() => { if (!ratesReady) dispatch(fetchRates()); }, []);

  const numericAmount      = parseFloat(fromAmount) || 0;
  const converted          = ratesReady ? convertAmount(numericAmount, fromCurrency, toCurrency, rates) : 0;
  const rate               = ratesReady ? convertAmount(1, fromCurrency, toCurrency, rates) : 0;
  const fromC              = getCurrency(fromCurrency);
  const toC                = getCurrency(toCurrency);
  const formattedConverted = ratesReady ? formatAmount(converted, toCurrency, symbols) : "—";
  const formattedRate      = ratesReady
    ? `1 ${fromCurrency} = ${formatAmount(rate, toCurrency, symbols)}`
    : "Loading…";

  const swap = () => { setFromCurrency(toCurrency); setToCurrency(fromCurrency); };

  const popularPairs = POPULAR_PAIRS.filter((c) => c !== fromCurrency).slice(0, 8);

  return (
    <div className="cosmoola-root" style={{ minHeight: "100vh", background: "#0a0a0f" }}>
      <GlobalStyles />

      {/* ════════ FIXED HEADER ════════ */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        height: 64,
        background: "rgba(9,9,11,0.88)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 28px",
      }}>
        {/* Logo with animated gradient pill */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="gb-wrap" style={{ "--gb-radius": "12px", "--gb-pad": "2px" } as React.CSSProperties}>
            <div className="gb-inner" style={{ "--gb-bg": "#0a0a0f", padding: "5px 8px", display: "flex", alignItems: "center", gap: 8, borderRadius: 10 } as React.CSSProperties}>
              <img src={logo} alt="Cosmoola" style={{ height: 36, width: 36, display: "block", borderRadius: 8, objectFit: "cover" }} />
            </div>
          </div>
        </div>

        {/* Nav */}
        <div style={{ display: "flex", gap: 6 }}>
          <button className="nav-btn active">Convert</button>
        </div>
      </header>

      {/* ════════ PAGE ════════ */}
      <div style={{ paddingTop: 80, paddingBottom: 48, maxWidth: 1060, margin: "0 auto", padding: "80px 24px 48px" }}>

        {/* Hero */}
        <div className="cosmoola-hero" style={{ textAlign: "center", padding: "36px 0 28px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 14px", borderRadius: 20,
            background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.25)",
            fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: "#a78bfa",
            marginBottom: 16,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981", animation: "pulse-dot 1.5s ease infinite" }} />
            Live rates updated
          </div>
          <h1 style={{
            fontSize: 44, fontWeight: 600, lineHeight: 1.15, marginBottom: 10,
            background: "linear-gradient(135deg, #f4f4f5 0%, #a78bfa 50%, #06b6d4 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            Exchange &amp; Transfer Worldwide
          </h1>
          <p style={{ fontSize: 15, color: "#71717a", maxWidth: 400, margin: "0 auto" }}>
            Real-time conversion across 150+ currencies
          </p>
        </div>

        {/* ── Main card with animated gradient border ── */}
        <div className="gb-wrap gb-wrap-slow" style={{ "--gb-radius": "20px", "--gb-pad": "1.5px" } as React.CSSProperties}>
          <div className="gb-inner" style={{ "--gb-bg": "#fff" } as React.CSSProperties}>

            <div
              className="cosmoola-two-col"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 360px",
                minHeight: 560,
              }}
            >
              {/* ──────── LEFT: CONVERTER ──────── */}
              <div style={{
                padding: "28px 28px",
                borderRight: "1px solid #f4f4f5",
                overflowY: "auto",
              }}>

                {/* Section label */}
                <p style={{ fontSize: 10, letterSpacing: "2.5px", color: "#a1a1aa", textTransform: "uppercase", marginBottom: 6 }}>
                  Currency Exchange
                </p>
                <h2 style={{ fontSize: 28, fontWeight: 600, color: "#09090b", lineHeight: 1.2, marginBottom: 20 }}>
                  Convert &amp; Transfer
                </h2>

                {/* Live rate banner */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderRadius: 8,
                  background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.15)",
                  marginBottom: 20,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981", animation: "pulse-dot 1.5s ease infinite" }} />
                    <span style={{ fontSize: 12, color: "#10b981" }}>Live rate</span>
                  </div>
                  <span style={{ fontSize: 13, color: "#09090b", fontWeight: 600 }}>
                    {formattedRate}
                  </span>
                </div>

                {/* FROM */}
                <p style={{ fontSize: 10, letterSpacing: "2.5px", color: "#a1a1aa", textTransform: "uppercase", marginBottom: 8 }}>From</p>
                <div className="conv-input-row" style={{ display: "flex", alignItems: "center", padding: "12px 16px", gap: 12, marginBottom: 4 }}>
                  <button
                    style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 14, minWidth: 88, cursor: "pointer", background: "transparent", border: "none", borderRight: "1px solid #e4e4e7" }}
                    onClick={() => setShowFromPicker(true)}
                  >
                    <span style={{ fontSize: 20 }}>{fromC.flag}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#09090b", letterSpacing: "0.3px" }}>{fromC.code}</span>
                    <span style={{ fontSize: 9, color: "#a1a1aa", marginLeft: 2 }}>▾</span>
                  </button>
                  <input
                    style={{ flex: 1, textAlign: "right", fontSize: 24, color: "#09090b", fontFamily: "'DM Mono', monospace", fontWeight: 400, border: "none", outline: "none", background: "transparent" }}
                    value={fromAmount}
                    onChange={(e) => setFromAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                    inputMode="decimal"
                  />
                </div>

                {/* SWAP */}
                <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                  <button className="swap-btn" onClick={swap} title="Swap currencies">⇅</button>
                </div>

                {/* TO */}
                <p style={{ fontSize: 10, letterSpacing: "2.5px", color: "#a1a1aa", textTransform: "uppercase", marginBottom: 8 }}>To</p>
                <div className="conv-input-row" style={{ display: "flex", alignItems: "center", padding: "12px 16px", gap: 12, marginBottom: 4, cursor: "pointer" }} onClick={() => setShowToPicker(true)}>
                  <button
                    style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 14, minWidth: 88, cursor: "pointer", background: "transparent", border: "none", borderRight: "1px solid #e4e4e7" }}
                    onClick={(e) => { e.stopPropagation(); setShowToPicker(true); }}
                  >
                    <span style={{ fontSize: 20 }}>{toC.flag}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#09090b", letterSpacing: "0.3px" }}>{toC.code}</span>
                    <span style={{ fontSize: 9, color: "#a1a1aa", marginLeft: 2 }}>▾</span>
                  </button>
                  <span style={{ flex: 1, textAlign: "right", fontSize: 24, color: "#7c3aed", fontFamily: "'DM Mono', monospace", fontWeight: 400 }}>
                    {ratesReady ? formattedConverted : "…"}
                  </span>
                </div>

                {/* Send Money — commented out, same as RN version */}
                {/*
                <button className="send-btn">Send Money →</button>
                */}

                {/* Popular pairs */}
                <p style={{ fontSize: 10, letterSpacing: "2.5px", color: "#a1a1aa", textTransform: "uppercase", marginTop: 28, marginBottom: 10 }}>
                  Popular · 1 {fromCurrency}
                </p>
                <div style={{ height: 1, background: "#f4f4f5", marginBottom: 4 }} />
                {ratesReady && popularPairs.map((code) => {
                  const c = getCurrency(code);
                  return (
                    <div key={code} className="pair-row-item" onClick={() => setToCurrency(code)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{c.flag}</span>
                        <div>
                          <div style={{ fontSize: 13, color: "#09090b", fontWeight: 600 }}>{c.code}</div>
                          <div style={{ fontSize: 11, color: "#a1a1aa", marginTop: 1 }}>{c.name}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 14, color: "#7c3aed", fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>
                        {formatAmount(convertAmount(1, fromCurrency, code, rates), code, symbols)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* ──────── RIGHT: CALCULATOR ──────── */}
              <div className="cosmoola-calc-card" style={{ background: "#fafafa", display: "flex", flexDirection: "column", position: "sticky", top: 64 }}>
                <div style={{ padding: "20px 20px 8px" }}>
                  <p style={{ fontSize: 10, letterSpacing: "2.5px", color: "#a1a1aa", textTransform: "uppercase" }}>Calculator</p>
                </div>

                {/* Display */}
                <div style={{
                  margin: "0 16px 8px",
                  padding: "12px 14px",
                  background: "#fff",
                  border: "1px solid #e4e4e7",
                  borderRadius: 10,
                  textAlign: "right",
                  minHeight: 72,
                }}>
                  <div style={{ fontSize: 12, color: "#a1a1aa", minHeight: 16, fontFamily: "'DM Mono', monospace" }}>{calc.expr}</div>
                  <div style={{ fontSize: 28, color: "#09090b", fontFamily: "'DM Mono', monospace", fontWeight: 400, marginTop: 4 }}>{calc.result}</div>
                </div>

                {/* Pad */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, flex: 1, borderTop: "1px solid #f0f0f0" }}>
                  {CALC_PAD.flat().map(([label, kind]) => (
                    <button
                      key={`${label}-${kind}`}
                      className={`calc-key ${label === "0" ? "zero" : ""} ${kind === "fn" ? "fn" : ""} ${kind === "op" ? "op" : ""} ${kind === "num" ? "num" : ""} ${kind === "eq" ? "eq" : ""}`}
                      onClick={() => kind === "fn" || kind === "eq" ? calc.fn(label) : calc.input(label)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ════════ PICKERS ════════ */}
      <CurrencyPickerModal
        visible={showFromPicker}
        selected={fromCurrency}
        onSelect={setFromCurrency}
        onClose={() => setShowFromPicker(false)}
        rates={rates}
        symbols={symbols}
        ratesReady={ratesReady}
      />
      <CurrencyPickerModal
        visible={showToPicker}
        selected={toCurrency}
        onSelect={setToCurrency}
        onClose={() => setShowToPicker(false)}
        rates={rates}
        symbols={symbols}
        ratesReady={ratesReady}
      />
    </div>
  );
}